import { FC } from 'react';
import classNames from 'classnames';

import { Badge } from '@/components/atoms/badge';
import { EnumCustomerType, TreezStore } from '@/types';

import styles from './storeCustomerTypeBadges.module.scss';

interface IStoreCustomerTypeBadgesProps {
  store: Partial<TreezStore> & {
    customerTypeAllow?: EnumCustomerType | string;
    customer_type_allow?: EnumCustomerType | string;
  };
  className?: string;
}

const normalizeCustomerTypeAllow = (
  store: IStoreCustomerTypeBadgesProps['store'],
) => {
  const raw = store.customerTypeAllow ?? store.customer_type_allow ?? '';
  return String(raw).trim().toUpperCase();
};

/** Which service badges to show for a store's `customerTypeAllow`. */
const getBadgeCustomerTypes = (normalized: string): EnumCustomerType[] => {
  switch (normalized) {
    case EnumCustomerType.ALL:
      return [EnumCustomerType.ADULT, EnumCustomerType.MEDICAL];
    case EnumCustomerType.MEDICAL:
      return [EnumCustomerType.MEDICAL];
    case EnumCustomerType.ADULT:
      return [EnumCustomerType.ADULT];
    default:
      return [];
  }
};

const StoreCustomerTypeBadges: FC<IStoreCustomerTypeBadgesProps> = ({
  store,
  className,
}) => {
  const customerTypes = getBadgeCustomerTypes(
    normalizeCustomerTypeAllow(store),
  );

  if (!customerTypes.length) return null;

  return (
    <div
      aria-label={`Store services: ${customerTypes.join(', ')}`}
      className={classNames(styles.badges, className)}
      role="group"
    >
      {customerTypes.map(type => (
        <Badge
          className={classNames(styles.badge_customer_type, {
            [styles.badge_medical]: type === EnumCustomerType.MEDICAL,
            [styles.badge_adult]: type === EnumCustomerType.ADULT,
          })}
          key={type}
        >
          {type}
        </Badge>
      ))}
    </div>
  );
};

export { StoreCustomerTypeBadges };
